/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2026-01-13 18:01:08
 */
import React, { useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../../app/hooks'
import { Button, Tag } from 'antd'
import classnames from 'classnames'
import {
  setGroupId,
  fetchGroupInfo,
  fetchGroupDeparment,
  fetchGroupCustomerConfig
} from '../../actions/group'
import { capturePromiseException } from '../../utils/sentryUtils'
import { pauseAllDownloadingTasks } from '../../utils/downloadTaskManager'

// 团队信息类型
interface GroupInfo {
  group_id: string
  group_name: string
  group_color?: string
  is_vip?: boolean
  avatar?: string
}

interface GroupProps {
  group: GroupInfo
  autoSwitchGroupId: string | null
  onTrack: () => void
}

function Group(props: GroupProps) {
  const { group, autoSwitchGroupId, onTrack } = props
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.main.user)
  const isOnlyInGroup = useAppSelector((state) => state.group.onlyInGroup)

  // 用于跟踪上一次的 autoSwitchGroupId
  const prevAutoSwitchGroupIdRef = useRef<string | null>(null)

  const handleChangeGroup = useCallback(async () => {
    onTrack()

    // 获取当前团队ID
    const currentGroupId = localStorage.getItem('group_id')

    // 如果切换的团队与当前团队不同，且当前团队有正在下载的任务，则暂停它们
    if (currentGroupId && currentGroupId !== group.group_id) {
      await pauseAllDownloadingTasks(currentGroupId)
    }

    localStorage.setItem('photoExportDownloadedTasks', 'null') //清除当前团队记录的已下载任务
    localStorage.setItem('photoExportTasks', 'null') //清除当前团队缓存的导出记录

    dispatch(
      fetchGroupInfo({
        group_id: group.group_id,
        only_in_group: isOnlyInGroup
      })
    )
      .then(() => {
        localStorage.setItem('group_id', group.group_id)
        dispatch(setGroupId(group.group_id))
        if (localStorage.getItem('enterPage') === 'verification') {
          localStorage.setItem('enterPage', '')
          navigate(`/verification`)
        } else if (
          localStorage.getItem('enterPage') === 'verificationOfficial'
        ) {
          localStorage.setItem('enterPage', '')
          navigate(`/verification?pageFrom=officialWebsite`)
        } else {
          navigate('/photo')
        }
      })
      .catch(capturePromiseException)
    dispatch(fetchGroupDeparment({ groupID: group.group_id }))
    dispatch(fetchGroupCustomerConfig({ groupID: group.group_id }))
  }, [dispatch, group, isOnlyInGroup, onTrack, navigate])

  // componentDidMount: 检查是否需要自动切换到这个团队
  useEffect(() => {
    if (autoSwitchGroupId === group.group_id) {
      handleChangeGroup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidUpdate: 检查 autoSwitchGroupId 是否有变化且匹配当前团队
  useEffect(() => {
    if (
      autoSwitchGroupId &&
      autoSwitchGroupId !== prevAutoSwitchGroupIdRef.current &&
      autoSwitchGroupId === group.group_id
    ) {
      handleChangeGroup()
    }
    prevAutoSwitchGroupIdRef.current = autoSwitchGroupId
  }, [autoSwitchGroupId, group, handleChangeGroup])

  const color = group.group_color

  return (
    <div className={classnames('group', { 'vip-group': group.is_vip })}>
      <div className="group-wrapper">
        {group.avatar === undefined || group.avatar === '' ? (
          <div className="group-icon" style={{ backgroundColor: color }}>
            {group.group_name.substring(0, 2)}
          </div>
        ) : (
          <div
            className="group-avatar"
            style={{
              backgroundImage: `url(${group.avatar})`
            }}
          ></div>
        )}

        <p className="group-name">
          {group.group_name}
          {group.is_vip && <Tag>高级</Tag>}
        </p>
      </div>
      <Button type={'primary'} onClick={handleChangeGroup}>
        进入
      </Button>
    </div>
  )
}

export default Group
